import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Text } from '@comicrelief/component-library';
import {
  FullWidthStyledInput,
  StyledButtonWithStates,
  StyledSelect,
  HorizontalLine,
  PostcodeSearchWrapper,
  ButtonWrapper
} from '../../components/global/Global.style';

import RouteProps from '@/types/shared-types';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useCartContext } from '@/services/cart/CartContext';
import { logger } from '@/services/logger/logger';
import { PostcodeLookupAddress } from '@/api/lookups/postcodeLookup';
import { usePaymentsConfig } from '@/services/paymentsConfig/usePaymentsConfig';
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useValidateTransactionPending } from '@/features/paymentProcess/useValidateTransactionPending';
import { clearErrorMessage } from '@/features/paymentProcess/transactionSlice';
import { useValidatePaymentSelectState } from '@/features/paymentSelect/useValidatePaymentSelectState';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';

import StepBack from '@/components/stepBack/StepBack';
import PostcodeLookup from '@/components/postcodeLookup/PostcodeLookup';
import PageHeader from '@/components/pageHeader/PageHeader';
import PrivacyNotice from '@/components/privacyNotice/PrivacyNotice';
import PayinSchoolLookup from '@/features/payin/PayinSchoolLookup';

import { updateUserDetailsFields } from './userDetailsSlice';
import { updatePayinFields } from '../payin/payinSlice';
import { formFields, getFormResolver, getAddressSectionHeaderText } from './userDetailsFields';
import { mapLookupToAddress } from '@/utils/addressMappingUtils';

import { SchoolLookupAddress } from '@/api/lookups/schoolLookup';

import {
  NameEmailWrapper,
  AddressWrapper,
  PostcodeLookupWrapper
} from './UserDetails.style';

import { AutofillButton } from '@/components/autofillButton/AutofillButton';
import { useUserDetailsAutofill } from './useUserDetailsAutofill';

// Is this field config actually used in the render?
import { formFields as schoolFields } from '../payin/payinPostalAddressFieldsSchool';
import { formFields as workFields } from '../payin/payinPostalAddressFieldsWork';

import { PAYIN_AUDIENCE_TYPES, FULFILMENT_TYPES, PAYIN_SCHOOL_SEGMENTS } from '@/constants/constants';
import { getPageHeaderInitialText, getPageHeaderSupplementaryText } from '@/utils/paymentTextUtils';
import { countriesOptions, DEFAULT_COUNTRY } from '@/locale';

const { PRIMARY, SECONDARY, COLLEGE } = PAYIN_SCHOOL_SEGMENTS;

const isNonProduction = import.meta.env.VITE_CLIENT === 'test';

const {
  firstName,
  lastName,
  email,
  confirmEmail,
  jobTitle,
  organisationName,
  establishmentType,
  establishmentName,
  country,
  address1,
  address2,
  address3,
  town,
  postcode,
} = formFields;

// Collect both sets of fields, removing duplicates:
const payinFields = [...new Set([...Object.keys(schoolFields), ...Object.keys(workFields)])];

function UserDetails({
  providerGroup = undefined,
  totalSteps,
  currencySymbol,
}: RouteProps) {
  const dispatch = useAppDispatch();
  const userDetailsState = useAppSelector(state => state.userDetails);
  const payinState = useAppSelector(state => state.payin);
  const shippingDetailsState = useAppSelector(state => state.shippingDetails);
  const { postalRewards } = shippingDetailsState;

  const isPayIn = !!payinState.audience;
  const { getCartFulfilmentType } = useCartContext();
  const isAARN = getCartFulfilmentType() === FULFILMENT_TYPES.AARN;

  // For use in journeys with postal items we don't want to send overseas.
  const isUKOnlyPostage = isAARN;

  const getCountryOptions = () => {
    if (isUKOnlyPostage) {
      return countriesOptions.filter(option => option.value === DEFAULT_COUNTRY);
    }
    return countriesOptions;
  };

  // Validate state is as expected, otherwise redirect to index or a previous step
  useValidateTransactionPending();
  useValidatePaymentSelectState();

  const journeyNavigate = useJourneyNavigate();

  const formMethods = useForm({
    mode: 'onBlur',
    resolver: getFormResolver(),
    context: {
      audience: payinState.audience,
    },
    defaultValues: {
      firstName: userDetailsState.firstName,
      lastName: userDetailsState.lastName,
      email: userDetailsState.email,
      confirmEmail: userDetailsState.confirmEmail,

      jobTitle: payinState.jobTitle,
      establishmentType: payinState.establishmentType,
      organisationName: payinState.organisationName,
      establishmentName: payinState.establishmentName,

      country: isUKOnlyPostage ? DEFAULT_COUNTRY : payinState.audience ? payinState.country : userDetailsState.country,
      address1: payinState.audience ? payinState.address1 : userDetailsState.address1,
      address2: payinState.audience ? payinState.address2 : userDetailsState.address2,
      address3: payinState.audience ? payinState.address3 : userDetailsState.address3,
      town: payinState.audience ? payinState.town : userDetailsState.town,
      postcode: payinState.audience ? payinState.postcode : userDetailsState.postcode,
    },
  });

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors: formErrors },
  } = formMethods;

  useEffect(() => {
    const watchInputs = watch((formValues, { name }) => {
      if (!name) {
        return;
      }

      // Some fields on this page update the payIn state, others update the userDetails state.
      // The address fields can be either userDetails or payIn state, depending on the journey; if payIn then these are payIn state fields.
      if (payinState.audience && payinFields.includes(name)) {
        dispatch(updatePayinFields({ [name]: formValues[name] }));
      } else {
        dispatch(updateUserDetailsFields({ [name]: formValues[name] }));
      }
    });
    return () => watchInputs.unsubscribe();
  }, [watch, dispatch, payinState.audience]);

  const onSubmit = async () => {
    try {
      dispatch(clearErrorMessage());
      journeyNavigate('next');
    } catch (error) {
      logger.error('Error during form submission', error);
    }
  };

  const onPostcodeLookupSelect = (result: PostcodeLookupAddress) => {
    try {
      const addressMapping = mapLookupToAddress(result);

      // Update form fields
      Object.entries(addressMapping).forEach(([field, value]) => {
        setValue(field as keyof typeof addressMapping, value as string);
      });

      // Update the correct state based on whether or not the user is paying in
      if (payinState.audience) {
        dispatch(updatePayinFields(addressMapping));
      } else {
        dispatch(updateUserDetailsFields(addressMapping));
      }

      // trigger() updates the fields state, otherwise the isValid will remain false. Without the "void", Typescript complains about a Promise issue to do with the React Hook Form internal submit function.
      void formMethods.trigger();
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Error mapping postcode lookup result');
    }
  };

  const onSchoolLookupSelect = (result: SchoolLookupAddress) => {
    try {
      const addressMapping = mapLookupToAddress(result, true);

      // Update form fields
      Object.entries(addressMapping).forEach(([field, value]) => {
        setValue(field as keyof typeof addressMapping, value as string);
      });

      dispatch(updatePayinFields({
        ...addressMapping,
        establishmentId: result.id,
      }));

      // trigger() updates the fields state, otherwise the isValid will remain false
      void formMethods.trigger();
    } catch (error) {
      logger.error(error instanceof Error ? error.message : 'Error mapping school lookup result');
    }
  };

  // Non-prod only: autofill the form to speed up manual testing
  const onAutofill = useUserDetailsAutofill(formMethods, isNonProduction);

  const { isDependencyEnabled } = usePaymentsConfig();
  const lookupsEnabled = isDependencyEnabled('plus'); // ('Postcode Look Up Service')
  const useSchoolsLookup = payinState?.establishmentType === PRIMARY || payinState?.establishmentType === SECONDARY || payinState?.establishmentType === COLLEGE;

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={event => void handleSubmit(onSubmit)(event)}
        noValidate
      >
        <PageHeader
          currentStep={3}
          totalSteps={totalSteps}
          headerText="Your details"
          initialText={getPageHeaderInitialText({ isPayIn, providerGroup, isAARN })}
          supplementaryText={getPageHeaderSupplementaryText({
            isPayIn, payinState, isAARN, postalRewards
          })}
          currencySymbol={currencySymbol}
        />

        <PaymentErrorText />

        <NameEmailWrapper>
          <FullWidthStyledInput
            label={firstName.label}
            id={firstName.id}
            errorMsg={formErrors?.firstName?.message}
            type={firstName.type}
            optional={false}
            {...register(firstName.id)}
          />

          <FullWidthStyledInput
            label={lastName.label}
            id={lastName.id}
            errorMsg={formErrors?.lastName?.message}
            type={lastName.type}
            optional={false}
            {...register(lastName.id)}
          />

          <FullWidthStyledInput
            label={email.label}
            id={email.id}
            errorMsg={formErrors?.email?.message}
            type={email.type}
            optional={false}
            {...register(email.id)}
          />

          <FullWidthStyledInput
            label={confirmEmail.label}
            id={confirmEmail.id}
            errorMsg={formErrors?.confirmEmail?.message}
            type={confirmEmail.type}
            optional={false}
            {...register(confirmEmail.id)}
          />
        </NameEmailWrapper>

        <HorizontalLine $margin="l" />

        <Text tag="h4" family="Montserrat" color="black">
          {getAddressSectionHeaderText(payinState.audience, isUKOnlyPostage)}
        </Text>

        <PostcodeLookupWrapper>
          {(payinState?.audience === PAYIN_AUDIENCE_TYPES.SCHOOL
            || payinState?.audience === PAYIN_AUDIENCE_TYPES.WORK) && (
            <FullWidthStyledInput
              label={jobTitle.label}
              id={jobTitle.id}
              type={jobTitle.type}
              errorMsg={formErrors?.jobTitle?.message}
              optional
              {...register(jobTitle.id)}
            />
          )}

          {payinState?.audience === PAYIN_AUDIENCE_TYPES.SCHOOL && (
            <StyledSelect
              label={establishmentType.label}
              id={establishmentType.id}
              options={establishmentType.options}
              errorMsg={formErrors?.establishmentType?.message}
              // As this is only actually rendered when it's needed,
              // there's no issue in marking it as always 'required';
              // the real validation is done within Yup
              optional={false}
              {...register(establishmentType.id)}
            />
          )}

          {payinState?.audience === PAYIN_AUDIENCE_TYPES.WORK && (
            <FullWidthStyledInput
              label={organisationName.label}
              id={organisationName.id}
              errorMsg={formErrors?.organisationName?.message}
              type={organisationName.type}
              optional={false}
              {...register(organisationName.id)}
            />
          )}

          <StyledSelect
            label={country.label}
            id={country.id}
            options={getCountryOptions()}
            errorMsg={formErrors?.country?.message}
            optional={false}
            {...register(country.id)}
            hideArrow={!!isUKOnlyPostage}
          />

          {((payinState.audience ? payinState.country : userDetailsState.country) === 'GB' && (lookupsEnabled)) && (
            <PostcodeSearchWrapper>
              {useSchoolsLookup ? (
                <PayinSchoolLookup onSelect={onSchoolLookupSelect} />
              ) : (
                <PostcodeLookup onSelect={onPostcodeLookupSelect} />
              )}
            </PostcodeSearchWrapper>
          )}
        </PostcodeLookupWrapper>

        <HorizontalLine $margin="m" />

        {((payinState.audience ? payinState.country : userDetailsState.country) === 'GB') && (
          <Text
            tag="h4"
            family="Montserrat"
            color="black"
            data-test="manualAddressHeading"
          >
            Or enter your address manually
          </Text>
        )}

        <AddressWrapper>
          {payinState?.audience === PAYIN_AUDIENCE_TYPES.SCHOOL && (
            <FullWidthStyledInput
              label={establishmentName.label}
              id={establishmentName.id}
              type={establishmentName.type}
              errorMsg={formErrors?.establishmentName?.message}
              optional={false}
              {...register(establishmentName.id)}
            />
          )}

          <FullWidthStyledInput
            label={address1.label}
            id={address1.id}
            errorMsg={formErrors?.address1?.message}
            type={address1.type}
            optional={false}
            {...register(address1.id)}
          />

          <FullWidthStyledInput
            label={address2.label}
            id={address2.id}
            errorMsg={formErrors?.address2?.message}
            type={address2.type}
            optional
            {...register(address2.id)}
          />

          <FullWidthStyledInput
            label={address3.label}
            id={address3.id}
            errorMsg={formErrors?.address3?.message}
            type={address3.type}
            optional
            {...register(address3.id)}
          />

          <FullWidthStyledInput
            label={town.label}
            id={town.id}
            errorMsg={formErrors?.town?.message}
            type={town.type}
            optional={false}
            {...register(town.id)}
          />

          <FullWidthStyledInput
            label={postcode.label}
            id={postcode.id}
            errorMsg={formErrors?.postcode?.message}
            type={postcode.type}
            optional={false}
            {...register(postcode.id)}
          />
        </AddressWrapper>

        <PrivacyNotice />

        <ButtonWrapper>
          <StyledButtonWithStates type="submit" data-test="userDetailsContinue">
            {payinState.audience ? 'Pay in by card' : 'Continue'}
          </StyledButtonWithStates>
        </ButtonWrapper>
      </form>

      <StepBack testId="userDetailsGoBack" />

      {isNonProduction && (
        <AutofillButton
          type="button"
          onClick={onAutofill}
          data-test="userDetailsAutofill"
        >
          Manual Testing: Fill form
        </AutofillButton>
      )}
    </FormProvider>
  );
}

export default UserDetails;
